import axios from 'axios';
import * as Sentry from '@sentry/vue';

const INITIAL_MAX_DELAY_MS = 10_000; // 10 seconds
const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds
const ERROR_RESET_DELAY_MS = 300_000; // 5 minutes
const SEND_HEARTBEAT_TIMEOUT_MS = 30_000; // 30 seconds,
const NEW_SESSION_DELAY_SECONDS = 60; // 60 seconds
const FORCE_NEW_SESSION_DELAY_SECONDS = 30 * 60; // 30 minutes

export const createHeartbeat = () => {
	const state = {
		eventId:                    null,
		startTimestamp:             null,
		lastSentHeartbeatTimestamp: null,
		stoppedBecauseOfErrors:     false,
		handleParallelViews:        null,
		playerBus:                  null,
		viewerId:                   null,
		lastStreamPosition:         null,
		payload:                    {},
		heartbeatCounter:           {},
	};

	let ticker = null;
	let timeout = null;

	return {
		setEventId(eventId) {
			state.eventId = eventId;
			return this;
		},
		setViewerId(viewerId) {
			state.viewerId = viewerId;
			return this;
		},
		setPlayerBus(playerBus) {
			state.playerBus = playerBus;
			return this;
		},
		setPayload(payload, merge = false) {
			state.payload = merge ? { ...state.payload, ...payload } : payload;
			return this;
		},
		setParallelViewHandler(handleParallelViews) {
			state.handleParallelViews = handleParallelViews;
			return this;
		},
		validateOrPanic() {
			if (!state.eventId) throw new Error('[HEARTBEAT] Fatal error: eventId is not set');
			if (!state.viewerId) throw new Error('[HEARTBEAT] Fatal error: viewerId is not set');
		},
		clearTimers() {
			if (ticker) clearInterval(ticker);
			if (timeout) clearTimeout(timeout);
		},
		resetAndStart() {
			this.reset();
			this.start();
		},
		reset() {
			this.clearTimers();
			state.startTimestamp = null;
			state.lastSentHeartbeatTimestamp = null;
		},
		start() {
			this.validateOrPanic();
			this.clearTimers();

			this.checkSessionState();
			state.stoppedBecauseOfErrors = false;
			const delay = this.getRandomNumberBetween(0, INITIAL_MAX_DELAY_MS);
			this.log(`delaying heartbeat interval by ${delay} milliseconds`);
			timeout = setTimeout(() => {
				this.log('initializing heartbeat interval');
				// Start heartbeat ticker, send initial heartbeat after an initial delay
				ticker = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
				this.sendHeartbeat();
			}, delay);
		},
		pause() {
			this.log('pausing heartbeat');
			this.clearTimers();
		},
		checkSessionState() {
			const lastHeartbeatSentSecondsAgo = state.lastSentHeartbeatTimestamp ? Date.now() / 1000 - state.lastSentHeartbeatTimestamp : null;

			// Create a new session timestamp when no start timestamp is available, or last heartbeat was more then 60 seconds ago
			// Do no start a new session if the heartbeat was stopped because of errors (i.e. server temporarily not available),
			// but always force start a new session after a certain threshold.
			if (!state.startTimestamp ||
				(lastHeartbeatSentSecondsAgo && lastHeartbeatSentSecondsAgo > NEW_SESSION_DELAY_SECONDS && !state.stoppedBecauseOfErrors) ||
				(lastHeartbeatSentSecondsAgo && lastHeartbeatSentSecondsAgo > FORCE_NEW_SESSION_DELAY_SECONDS && state.stoppedBecauseOfErrors)
			) {
				state.startTimestamp = Math.floor(Date.now() / 1000);
				this.log('new session initialized');
			}
		},
		sendHeartbeat() {
			this.validateOrPanic();
			this.checkSessionState();

			let telemetry = {};
			let isPlaying = false;

			if (!state.heartbeatCounter[state.eventId]) {
				state.heartbeatCounter[state.eventId] = 1;
			}

			try {
				// Detect when the stream has stopped playing due to unexpected errors
				isPlaying = state.playerBus && state.playerBus.position > 0 && !(state.playerBus.position === state.lastStreamPosition);
				const videoHeight = state.playerBus?.player?.getCurrentVideoHeight();
				const audioTrack = state.playerBus?.player?.getCurrentAudioTrack();
				telemetry = {
					qualityLevel:       videoHeight || 0,
					fatalMediaError:    state.playerBus?.player?.errorCount?.fatalMediaError || 0,
					networkError:       state.playerBus?.player?.errorCount?.networkError || 0,
					bufferStalledError: state.playerBus?.player?.errorCount?.bufferStalledError || 0,
					requestFailedError: state.playerBus?.player?.errorCount?.requestFailedError || 0,
					firstFrameLoaded:   state.playerBus?.player?.hasLoadedFirstFrame || false,
					heartbeatCount:     state.heartbeatCounter[state.eventId],
					audioTrack:         audioTrack || '-',
				};
			} catch (e) {
				console.error(e);
			}

			state.lastStreamPosition = state.playerBus ? state.playerBus.position : null;
			state.lastSentHeartbeatTimestamp = Date.now() / 1000;

			this.sendPlayHeartbeat(state.eventId, state.viewerId, state.startTimestamp, isPlaying, telemetry)
				.then((response) => {
					if (response.data?.status !== 'success') {
						this.log('Unexpected response from heartbeat endpoint: ' + JSON.stringify(response.data), 'error');
						Sentry.captureMessage('Heartbeat: unexpected response from heartbeat endpoint', {
							level: 'error',
							tags:  {
								eventId: state.eventId,
								group:   'heartbeat',
							},
							extra: {
								errorDetails: response.data,
								timestamp:    new Date().toISOString(),
							},
						});
					}

					const player = state.playerBus?.player;
					if (player) {
						player.resetErrorCount();
					}
					state.heartbeatCounter[state.eventId]++;
				})
				.catch((error) => {
					if (error.status === 403 && error.data && error.data && error.data.reason === 'parallelStream' && state.handleParallelViews) {
						state.handleParallelViews(error.data);
					} else {
						this.log('Error from heartbeat endpoint. Status code=' + error.status + ': ' + JSON.stringify(error.data), 'error');
						Sentry.captureMessage('Heartbeat: error response from heartbeat endpoint (status code=' + error.status + ')', {
							level: 'error',
							tags:  {
								eventId: state.eventId,
								group:   'heartbeat',
							},
							extra: {
								errorDetails: error,
								timestamp:    new Date().toISOString(),
							},
						});
					}

					state.stoppedBecauseOfErrors = true;
					this.clearTimers();
					timeout = setTimeout(() => this.start(), ERROR_RESET_DELAY_MS);
				});
		},
		getRandomNumberBetween(min, max) {
			return Math.random() * (max - min) + min;
		},
		async sendPlayHeartbeat(eventId, viewerId, sessionStartTs, isPlaying, telemetry) {
			const url = `/api/v1/event/${eventId}/play/${viewerId}/${sessionStartTs}`;
			const body = {
				...{
					viewerId:       viewerId,
					sessionStartTs: sessionStartTs.toString(),
					source:         window.location.href,
					referrer:       document.referrer !== window.location.href ? document.referrer : null,
					ignore:         !!window.Cypress,
					isPlaying:      isPlaying,
					telemetry,
				},
				...state.payload,
			};
			const res = await axios.post(
				url,
				body,
				{
					timeout: SEND_HEARTBEAT_TIMEOUT_MS,
				},
			);
			return res;
		},
		log(msg, level = 'info') {
			msg = `[HEARTBEAT][${new Date().toLocaleString()}] ${msg} (eventId=${state.eventId}, viewerId=${state.viewerId}, startTimestamp=${state.startTimestamp})`;
			if (typeof console[level] === 'function') {
				console[level](msg);
			} else {
				console.log('Unknown log level:', level);
				console.log(msg);
			}
		},
	};
};
